import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';

export default function ConnectScreen() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [contactMethod, setContactMethod] = useState<'email' | 'phone'>('email');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !email || !message) {
      alert('Please fill in all required fields');
      return;
    }

    if (contactMethod === 'phone' && !phone) {
      alert('Please provide a phone number for callback');
      return;
    }

    setLoading(true);

    try {
      const { error: insertError } = await supabase
        .from('case_manager_requests')
        .insert({
          name,
          email,
          phone: phone || '',
          message,
          preferred_contact: contactMethod,
        });

      if (insertError) throw insertError;

      alert(`Your request has been received. A case manager will contact you shortly via ${contactMethod}.`);
      setName('');
      setEmail('');
      setPhone('');
      setMessage('');
    } catch (err) {
      alert('Failed to submit request. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailContact = () => {
    window.location.href = 'mailto:harvesthousetobique@gmail.com';
  };

  const handleFacebookContact = () => {
    window.open('https://www.facebook.com/HarvestHouseTobique', '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#B85C38] py-12 px-6 text-center">
        <h1 className="text-4xl font-bold text-white mb-3">Connect With Us</h1>
        <p className="text-lg text-white">Reach out to speak with a case manager</p>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/" className="inline-block text-gray-600 hover:text-gray-900 mb-6">
          ← Back to Home
        </Link>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 shadow-md mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Send us a Message</h2>

          <label className="block text-sm font-semibold text-gray-700 mb-2 mt-4">
            Your Name *
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg p-3"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <label className="block text-sm font-semibold text-gray-700 mb-2 mt-4">
            Email Address *
          </label>
          <input
            type="email"
            className="w-full border border-gray-300 rounded-lg p-3"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <label className="block text-sm font-semibold text-gray-700 mb-2 mt-4">
            Phone Number
          </label>
          <input
            type="tel"
            className="w-full border border-gray-300 rounded-lg p-3"
            placeholder="(123) 456-7890"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />

          <label className="block text-sm font-semibold text-gray-700 mb-3 mt-4">
            How should we contact you? *
          </label>
          <div className="flex gap-6 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <div className="relative">
                <input
                  type="radio"
                  name="contactMethod"
                  value="email"
                  checked={contactMethod === 'email'}
                  onChange={() => setContactMethod('email')}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-full border-2 border-[#B85C38] flex items-center justify-center ${
                  contactMethod === 'email' ? 'bg-white' : 'bg-transparent'
                }`}>
                  {contactMethod === 'email' && <div className="w-2.5 h-2.5 rounded-full bg-[#B85C38]"></div>}
                </div>
              </div>
              <span className="text-gray-700">Email</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <div className="relative">
                <input
                  type="radio"
                  name="contactMethod"
                  value="phone"
                  checked={contactMethod === 'phone'}
                  onChange={() => setContactMethod('phone')}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-full border-2 border-[#B85C38] flex items-center justify-center ${
                  contactMethod === 'phone' ? 'bg-white' : 'bg-transparent'
                }`}>
                  {contactMethod === 'phone' && <div className="w-2.5 h-2.5 rounded-full bg-[#B85C38]"></div>}
                </div>
              </div>
              <span className="text-gray-700">Phone</span>
            </label>
          </div>

          <label className="block text-sm font-semibold text-gray-700 mb-2 mt-4">
            Your Message *
          </label>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 h-32"
            placeholder="Tell us about yourself and how we can help..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#B85C38] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#A34C28] transition-colors mt-6 disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Connect With a Case Manager'}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            We take your privacy seriously. Your information will only be used to help you.
          </p>
        </form>

        <div className="bg-[#E8DDD0] rounded-xl p-8 border-l-4 border-[#B85C38] mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-3">What is a Case Manager?</h3>
          <p className="text-gray-700 leading-relaxed">
            A case manager is a dedicated person who helps you navigate your recovery journey. They provide support, resources, and guidance tailored to your specific needs.
          </p>
        </div>

        <div className="bg-[#E8DDD0] rounded-xl p-8 border-l-4 border-[#B85C38] mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-3">How It Works</h3>
          <div className="space-y-2 text-gray-700">
            <p>1. Fill out this form with your information</p>
            <p>2. A case manager will reach out within 24 hours</p>
            <p>3. We'll discuss your needs and create a plan</p>
            <p>4. Ongoing support as you move forward</p>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-xl p-8 border-l-4 border-yellow-400 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-3">Direct Contact</h3>
          <p className="text-gray-700 mb-4">You can also reach us directly:</p>

          <button
            onClick={handleEmailContact}
            className="w-full bg-white text-[#B85C38] py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors mb-3"
          >
            harvesthousetobique@gmail.com
          </button>

          <button
            onClick={handleFacebookContact}
            className="w-full bg-white text-[#B85C38] py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Follow us on Facebook
          </button>
        </div>

        <div className="bg-[#E8DDD0] rounded-xl p-8 border border-[#B85C38] mb-6">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">You Are Not Alone</h3>
          <p className="text-gray-700 leading-relaxed mb-4 text-center">
            Whether you're struggling with addiction, feeling lonely, dealing with hardship, or simply looking for community,
            Harvest House Tobique is here for you. We believe in your ability to heal and grow.
          </p>
          <p className="text-gray-600 italic text-center">
            "Connecting people of compassion with people in need to make a difference"
          </p>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-8 border-2 border-amber-300 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">Our Family of Ministries</h3>
          <p className="text-gray-700 text-center mb-8 leading-relaxed">
            We're part of a network of compassionate ministries serving communities across Atlantic Canada
          </p>

          <div className="bg-gradient-to-r from-[#B85C38] to-[#A34C28] rounded-xl p-8 mb-6 shadow-xl border-4 border-amber-400">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center">
                <span className="text-[#B85C38] font-bold text-lg">★</span>
              </div>
              <div className="flex-1">
                <h4 className="text-2xl font-bold text-white mb-2">Our Church - Fredericton, NB</h4>
                <p className="text-amber-50 leading-relaxed mb-4">
                  Our sister ministry serving the homeless community in Fredericton. A branch of Harvest House Tobique,
                  bringing hope, meals, and support to those in need in the capital city.
                </p>
                <a
                  href="https://www.facebook.com/OurChurchFredericton"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-white text-[#B85C38] px-6 py-3 rounded-lg font-bold hover:bg-amber-50 transition-all transform hover:scale-105 shadow-lg"
                >
                  Visit on Facebook →
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#8B7355] hover:shadow-lg transition-shadow">
              <h4 className="text-xl font-bold text-gray-900 mb-2">Harvest House Woodstock</h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Supporting recovery and community wellness in Woodstock, NB
              </p>
              <a
                href="https://www.facebook.com/HarvestHouseWoodstock"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-[#B85C38] font-semibold hover:text-[#A34C28] transition-colors"
              >
                Visit on Facebook →
              </a>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#8B7355] hover:shadow-lg transition-shadow">
              <h4 className="text-xl font-bold text-gray-900 mb-2">Harvest House Atlantic</h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Supporting recovery and community wellness throughout Atlantic Canada
              </p>
              <a
                href="https://www.harvesthouseatlantic.org"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-[#B85C38] font-semibold hover:text-[#A34C28] transition-colors"
              >
                Learn More →
              </a>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#8B7355] hover:shadow-lg transition-shadow">
              <h4 className="text-xl font-bold text-gray-900 mb-2">ShekinahGo</h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Mobile outreach ministry bringing hope and support to communities in need
              </p>
              <a
                href="https://www.facebook.com/ShekinahGo"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-[#B85C38] font-semibold hover:text-[#A34C28] transition-colors"
              >
                Visit on Facebook →
              </a>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#8B7355] hover:shadow-lg transition-shadow">
              <h4 className="text-xl font-bold text-gray-900 mb-2">Newman House Recovery Center</h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Providing safe housing and support for men in recovery
              </p>
              <a
                href="https://www.facebook.com/NewmanHouseRecovery"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-[#B85C38] font-semibold hover:text-[#A34C28] transition-colors"
              >
                Visit on Facebook →
              </a>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#8B7355] hover:shadow-lg transition-shadow">
              <h4 className="text-xl font-bold text-gray-900 mb-2">Village of Hope</h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Building community and providing transitional housing for those in recovery
              </p>
              <a
                href="https://www.facebook.com/VillageofHopeNB"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-[#B85C38] font-semibold hover:text-[#A34C28] transition-colors"
              >
                Visit on Facebook →
              </a>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#8B7355] hover:shadow-lg transition-shadow">
              <h4 className="text-xl font-bold text-gray-900 mb-2">Teen Challenge</h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Faith-based recovery program for teens and young adults facing life challenges
              </p>
              <a
                href="https://www.teenchallenge.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-[#B85C38] font-semibold hover:text-[#A34C28] transition-colors"
              >
                Learn More →
              </a>
            </div>
          </div>

          <p className="text-center text-gray-600 text-sm mt-6 italic">
            Together, we're making a difference across Atlantic Canada
          </p>
        </div>
      </div>
    </div>
  );
}
